import { useEffect, useRef } from "react";

/**
 * Custom hook to handle modal closing on ESC key press and click outside
 * @param {boolean} isOpen - Whether the modal is currently open
 * @param {Function} onClose - Function to call when modal should close
 * @param {boolean} closeOnClickOutside - Whether to close when clicking outside (default: true)
 * @param {boolean} closeOnEsc - Whether to close when pressing ESC (default: true)
 */
export const useModal = (
  isOpen,
  onClose,
  closeOnClickOutside = true,
  closeOnEsc = true
) => {
  const modalRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event) => {
      if (closeOnEsc && event.key === "Escape") {
        onClose();
      }
    };

    const handleClickOutside = (event) => {
      if (
        closeOnClickOutside &&
        modalRef.current &&
        !modalRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    // Add event listeners
    document.addEventListener("keydown", handleEscape);
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, closeOnClickOutside, closeOnEsc]);

  return modalRef;
};
